import type { CSSProperties } from 'react';
import { AlertTriangle, CheckCircle2, Info, Loader2, X, type LucideIcon } from 'lucide-react';
import { Toaster } from 'sonner';
import { cn } from '@/lib/cn';

/**
 * Sonner's own look (richColors' saturated green/red banners) fights the
 * parchment palette, so the toasts are rendered `unstyled` and dressed with the
 * same tokens as Card: parchment surface, hairline border, card shadow. Only
 * the icon chip and the border carry the status colour.
 *
 * `unstyled` drops sonner's visual CSS (gated on [data-styled='true']) but keeps
 * its positioning, stacking, swipe-to-dismiss and enter/exit animation.
 */
type Tone = 'success' | 'danger' | 'warn' | 'accent';

const TONE_CHIP: Record<Tone, string> = {
  success: 'bg-success/15 text-success',
  danger: 'bg-danger/15 text-danger',
  warn: 'bg-warn/15 text-warn',
  accent: 'bg-accent/15 text-accent',
};

function ToastIcon({ tone, icon: Icon, spin }: { tone: Tone; icon: LucideIcon; spin?: boolean }) {
  return (
    <span className={cn('grid h-7 w-7 place-items-center rounded-md', TONE_CHIP[tone])}>
      <Icon className={cn('h-4 w-4', spin && 'animate-spin')} aria-hidden="true" />
    </span>
  );
}

/**
 * The close button is placed by sonner's own CSS variables rather than by
 * classes: its rules use `:where(... [data-close-button])`, which sets `left`
 * and a translate that would drag the button outside the card no matter what
 * utilities are applied. Colours still need `!` — that one background rule is a
 * plain descendant selector and out-specifies a single utility class.
 */
const CLOSE_BUTTON_POSITION = {
  '--toast-close-button-start': 'unset',
  '--toast-close-button-end': '0.5rem',
  '--toast-close-button-transform': 'none',
  // CSS custom properties aren't part of the CSSProperties index signature.
} as CSSProperties;

export function AppToaster() {
  return (
    <Toaster
      position="top-center"
      offset={12}
      gap={8}
      closeButton
      style={CLOSE_BUTTON_POSITION}
      icons={{
        success: <ToastIcon tone="success" icon={CheckCircle2} />,
        error: <ToastIcon tone="danger" icon={AlertTriangle} />,
        warning: <ToastIcon tone="warn" icon={AlertTriangle} />,
        info: <ToastIcon tone="accent" icon={Info} />,
        loading: <ToastIcon tone="accent" icon={Loader2} spin />,
        close: <X className="h-3.5 w-3.5" aria-hidden="true" />,
      }}
      toastOptions={{
        unstyled: true,
        classNames: {
          toast:
            'relative flex w-full items-start gap-3 rounded-lg border border-parchment-border bg-parchment-light p-3 pr-10 font-sans shadow-parchment-card',
          content: 'min-w-0 flex-1',
          title: 'text-sm font-medium leading-snug text-parchment-ink',
          description: 'mt-0.5 text-xs leading-snug text-parchment-muted',
          icon: 'shrink-0',
          // Status is a tinted hairline, not a filled banner.
          success: 'border-success/40',
          error: 'border-danger/40',
          warning: 'border-warn/40',
          info: 'border-accent/40',
          closeButton:
            'top-2 grid h-6 w-6 place-items-center rounded-md !border-transparent !bg-transparent !text-parchment-subtle transition-colors hover:!bg-parchment-dark/60 hover:!text-parchment-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent coarse:h-8 coarse:w-8',
          actionButton:
            'ml-2 shrink-0 rounded-md bg-parchment-ink px-2.5 py-1 text-xs font-medium text-parchment-light transition-opacity hover:opacity-90',
          cancelButton:
            'ml-2 shrink-0 rounded-md px-2.5 py-1 text-xs font-medium text-parchment-muted transition-colors hover:bg-parchment-dark/60',
        },
      }}
    />
  );
}
